// Users admin — generate demo accounts, promote/demote the admin role, and
// impersonate, through the BFF's `/api/admin/users/*` (#261, ADR 0062).
//
// Fetch-wrapper shape copied from catalog.ts (#260): network failure ->
// "error", 401 -> "unauthorized" (send back through sign-in), defensive
// JSON parse, non-2xx pulls `payload.error`.

import { storefrontOrigin } from "./config";

export interface AdminUser {
  sub: string;
  name?: string;
  email?: string;
  role: "customer" | "admin";
}

export type UsersResult<T> =
  | { kind: "ok"; value: T }
  | { kind: "error"; message: string }
  | { kind: "unauthorized" };

async function call<T>(
  input: RequestInfo,
  init: RequestInit | undefined,
  unwrap: (payload: unknown) => T | undefined,
): Promise<UsersResult<T>> {
  let res: Response;
  try {
    res = await fetch(input, { credentials: "include", ...init });
  } catch (err) {
    return { kind: "error", message: err instanceof Error ? err.message : "request failed (network)" };
  }

  if (res.status === 401) {
    return { kind: "unauthorized" };
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const apiError =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `${res.status} ${res.statusText}`.trim();
    return { kind: "error", message: apiError };
  }

  const value = unwrap(payload);
  if (value === undefined) {
    return { kind: "error", message: "unexpected response shape" };
  }
  return { kind: "ok", value };
}

export function fetchUsers(): Promise<UsersResult<AdminUser[]>> {
  return call<AdminUser[]>("/api/admin/users", undefined, (p) =>
    p && typeof p === "object" && "users" in p ? (p as { users: AdminUser[] }).users : undefined,
  );
}

/** No form, per the ticket — just a count. Clamped server-side (max 50). */
export function generateUsers(count: number): Promise<UsersResult<{ count: number; users: AdminUser[] }>> {
  return call<{ count: number; users: AdminUser[] }>(
    `/api/admin/users/:generate?count=${encodeURIComponent(count)}`,
    { method: "POST" },
    (p) =>
      p && typeof p === "object" && "count" in p && "users" in p
        ? (p as { count: number; users: AdminUser[] })
        : undefined,
  );
}

function setRole(sub: string, action: "promote" | "demote"): Promise<UsersResult<{ sub: string; role: string }>> {
  return call<{ sub: string; role: string }>(
    `/api/admin/users/${encodeURIComponent(sub)}/${action}`,
    { method: "POST" },
    (p) =>
      p && typeof p === "object" && "sub" in p && "role" in p
        ? (p as { sub: string; role: string })
        : undefined,
  );
}

export const promote = (sub: string) => setRole(sub, "promote");
export const demote = (sub: string) => setRole(sub, "demote");

/**
 * Mints a one-time impersonation token and returns the storefront URL that
 * exchanges it for a real customer session there (a cross-origin cookie
 * handoff — this can't just be an API response, the browser has to
 * navigate). `null` when the storefront's origin isn't configured
 * (storefront-ui disabled) — the caller should hide the impersonate button
 * in that case rather than call this at all.
 */
export async function impersonateLink(sub: string): Promise<UsersResult<string>> {
  const origin = storefrontOrigin();
  if (!origin) {
    return { kind: "error", message: "storefront isn't configured on this deployment" };
  }

  const result = await call<string>(
    `/api/admin/users/${encodeURIComponent(sub)}/impersonate`,
    { method: "POST" },
    (p) => (p && typeof p === "object" && "token" in p ? (p as { token: string }).token : undefined),
  );
  if (result.kind !== "ok") {
    return result;
  }
  return { kind: "ok", value: `${origin}/auth/impersonate/exchange?token=${encodeURIComponent(result.value)}` };
}
